// @ts-nocheck

import type { PresetKey, PresetKeys } from 'client/js/main/screens/LinkCreator/types';
import { archiveAdsLink, getAdLinks } from 'client/js/redux/domains/AdLinks/actions';
import queryString from 'query-string';
import * as R from 'ramda';
import type { FlipperFlagValues } from '../../../../../shared/ProductFlipperFlag.service';
import {
  FACEBOOK_MACHINE_NAME,
  GOOGLE_ADWORDS_MACHINE_NAME,
  TIKTOK_SAN_MACHINE_NAME,
} from '../../../../../shared/constants/adPartnerConstants';
import { isDeeplinkNoAttributionPartner } from '../../../../../shared/validators/deeplinkNoAttributionValidator';
import { fetchTuneSiteData } from '../../../common/actions/AppActions';
import { getDruidFiltersByDimension } from '../../../common/actions/DruidActions';
import type { TuneOptionsByPlatform } from '../../../common/types/appTypes';
import {
  clearScraperData,
  getAdOntology,
  getAndroidAppInfo,
  getEnabledAdNetworks,
  getIOSAppInfo,
  getLinkById,
  getTopCampaign,
  getTopChannel,
  getTopFeature,
  resetStore,
  saveLink,
  updateLinkData,
} from '../../actions/LinkCreatorActions';
import { loadSDKInfo, loadSDKLatestInfo } from '../../actions/SDKInfoActions';
import { fetchLinkData } from '../../actions/ScraperAction';
import { PRESET_FIELDS } from '../../reducers/LinkCreatorReducer/utils';
import AppsStore from '../../stores/AppsStore_LEGACY';
import type { AdOntology, AdPartner } from '../../types/adPartnerTypes';
import { AD_FORMATS, IMPRESSION_PARAM_BLACKLIST, SECONDARY_AD_FORMATS } from './constants/adLinkConstants';
import { buildTuneQueryObject, expandTuneQueryParams, getTuneBase, getTuneDomain } from './tuneUtils';

export const DEBOUNCE_TIME = 1000;

const PRESET_KEYS: PresetKeys = [
  {
    value: '$deeplink_path',
    label: '$deeplink_path',
    tooltip:
      "Set the deep link path for all platforms - so you don't have to enable it by platform. When the Branch SDK receives a link with this parameter set, it will automatically load the custom URI path contained within",
  },
  {
    value: '$ios_deeplink_path',
    label: '$ios_deeplink_path',
    tooltip:
      'Set the deep link path for iOS apps. When the Branch SDK receives a link with this parameter set, it will automatically load the custom iOS URI path contained within',
  },
  {
    value: '$android_deeplink_path',
    label: '$android_deeplink_path',
    tooltip:
      'Set the deep link path for Android apps. When the Branch SDK receives a link with this parameter set, it will automatically load the custom Android URI path contained within',
  },
  {
    value: '$desktop_deeplink_path',
    label: '$desktop_deeplink_path',
    tooltip: 'Set the deep link path for Desktop apps. You will have to fetch this parameter and route the user accordingly',
  },
  {
    value: '$click_install_window_days',
    label: '$click_install_window_days',
    tooltip:
      'Time between a click and an install or reinstall. (i.e., https://branchster.app.link/hpNVE52gxE?$click_install_window_days=3)',
  },
  {
    value: '$click_session_start_window_days',
    label: '$click_session_start_window_days',
    tooltip:
      'Time between a click and an open or web session start. (i.e., https://branchster.app.link/hpNVE52gxE?$click_session_start_window_days=7)',
  },
  {
    value: '$click_conversion_window_days',
    label: '$click_conversion_window_days',
    tooltip:
      'Time between a click and a conversion event. Conversion events include commerce events (e.g. purchase, add to cart), all custom events, and all view events like pageviews & content views. (i.e., https://branchster.app.link/hpNVE52gxE?$click_session_start_window_days=30)',
  },
  {
    value: '$impression_install_window_days',
    label: '$impression_install_window_days',
    tooltip:
      'Time between an ad impression and an install or reinstall. (i.e., https://branchster.app.link/hpNVE52gxE?$impression_install_window_days=3)',
  },
  {
    value: '$impression_session_start_window_days',
    label: '$impression_session_start_window_days',
    tooltip:
      'Time between an ad impression and an open or web session start. (i.e., https://branchster.app.link/hpNVE52gxE?$impression_session_start_window_days=1)',
  },
  {
    value: '$impression_conversion_window_days',
    label: '$impression_conversion_window_days',
    tooltip:
      'Time between an ad impression and a conversion event. Conversion events include commerce events (e.g. purchase, add to cart), all custom events, and all view events like pageviews & content views. (i.e., https://branchster.app.link/hpNVE52gxE?$impression_session_start_window_days=7)',
  },
  {
    value: '$household_measurement_window_days',
    label: '$household_measurement_window_days',
    tooltip:
      'Time between a household device’s ad engagement and an install, open, web session start, and conversion event. Conversion events include commerce events (e.g. purchase, add to cart), all custom events and all view events (e.g. pageviews, content views). Fixed selection of 1, 2 or 3 days. (i.e., https://branchster.app.link/hpNVE52gxE?$household_measurement_window_days=3)',
  },
  {
    value: '$fallback_url',
    label: '$fallback_url',
    tooltip:
      "Change the redirect endpoint for all platforms - so you don't have to enable it by platform. Note that Branch will forward all robots to this URL, which overrides any OG tags entered in the link. System-wide Default URL (set in Configuration)",
  },
  {
    value: '$ipad_url',
    label: '$ipad_url',
    tooltip: 'Change the redirect endpoint for iPads $ios_url value',
  },
  {
    value: '$samsung_url',
    label: '$samsung_url',
    tooltip:
      'Redirect to Samsung Galaxy Store on Samsung devices. Only link level control. Format should be http://www.samsungapps.com/appquery/appDetail.as?appId=YOUR.PACKAGE.NAME',
  },
  {
    value: '$windows_phone_url',
    label: '$windows_phone_url',
    tooltip: 'Change the redirect endpoint for Windows OS Windows Phone default URL (set in Configuration)',
  },
  {
    value: '$blackberry_url',
    label: '$blackberry_url',
    tooltip: 'Change the redirect endpoint for Blackberry OS BlackBerry default URL (set in Configuration)',
  },
  {
    value: '$fire_url',
    label: '$fire_url',
    tooltip: 'Change the redirect endpoint for Amazon Fire OS Fire default URL (set in Configuration)',
  },
  {
    value: '$ios_wechat_url',
    label: '$ios_wechat_url',
    tooltip: 'Change the redirect endpoint for WeChat on iOS devices $ios_url value',
  },
  {
    value: '$android_wechat_url',
    label: '$android_wechat_url',
    tooltip: 'Change the redirect endpoint for WeChat on Android devices $android_url value',
  },
  {
    value: '$huawei_url',
    label: '$huawei_url',
    tooltip: 'Change the redirect endpoint for Huawei devices',
  },
  {
    value: '$web_only',
    label: '$web_only',
    tooltip: 'Force to open the $fallback_url instead of the app',
  },
  {
    value: '$uri_redirect_mode',
    label: '$uri_redirect_mode',
    tooltip:
      "This is the default value that yields the standard behavior where we don't try to open the app if the user can see an error.",
  },
  {
    value: '$match_duration',
    label: '$match_duration',
    tooltip:
      'Lets you control the snapshotting match timeout (the time that a click will wait for an app open to match) also known as attribution window. Specified in seconds',
  },
  {
    value: '$always_deeplink',
    label: '$always_deeplink',
    tooltip:
      'Set to false to make links always fall back to your mobile site. Does not apply to Universal Links or Android App Links.',
  },
  {
    value: '$ios_redirect_timeout',
    label: '$ios_redirect_timeout',
    tooltip:
      'Control the timeout that the client-side JS waits after trying to open up the app before redirecting to the App Store. Specified in milliseconds',
  },
  {
    value: '$android_redirect_timeout',
    label: '$android_redirect_timeout',
    tooltip:
      'Control the timeout that the client side JS waits after trying to open up the app before redirecting to the Play Store. Specified in milliseconds',
  },
  {
    value: '$custom_sms_text',
    label: '$custom_sms_text',
    tooltip:
      'Text for SMS link sent for desktop clicks to this link. Must contain {{ link }} Value of Text me the app page in Settings',
  },
  {
    value: '$deeplink_no_attribution',
    label: '$deeplink_no_attribution',
    tooltip: 'Set to true for the links to only support deep linking without any attribution for that link.',
  },
  {
    value: '$publicly_indexable',
    label: '$publicly_indexable',
    tooltip: 'Cannot modify here. Needs to be set by the Branch Universal Object',
  },
  {
    value: '$canonical_identifier',
    label: '$canonical_identifier',
    tooltip:
      'This is the unique identifier for content that will help Branch dedupe across many instances of the same thing. Suitable options: a website with pathing, or a database with identifiers for entities',
  },
  {
    value: '$canonical_url',
    label: '$canonical_url',
    tooltip: 'The canonical URL, used for SEO purposes',
  },
  {
    value: '$content_type',
    label: '$content_type',
    tooltip:
      'This is a label for the type of content present. Apple recommends that you use uniform type identifier as described here',
  },
  {
    value: '$do_not_process',
    label: '$do_not_process',
    tooltip:
      'This will prevent click tracking and storage of link analytics. Deep link data will still flow into the app from link click to app open.',
  },
  {
    value: '$ios_passive_deepview',
    label: '$ios_passive_deepview',
    tooltip: 'The name of the template to use for iOS.',
  },
  {
    value: '$android_passive_deepview',
    label: '$android_passive_deepview',
    tooltip: 'The name of the template to use for Android.',
  },
  {
    value: '$og_image_width',
    label: '$og_image_width',
    tooltip: "Set the image's width in pixels for social media displays",
  },
  {
    value: '$og_image_height',
    label: '$og_image_height',
    tooltip: "Set the image's height in pixels for social media displays",
  },
  {
    value: '$og_video',
    label: '$og_video',
    tooltip: 'Set a video as it will be seen in social media displays',
  },
  {
    value: '$og_url',
    label: '$og_url',
    tooltip: 'Set the base URL of the link as it will be seen in social media displays',
  },
  {
    value: '$og_type',
    label: '$og_type',
    tooltip:
      "Set the type of custom card format link as it will be seen in social media displays. Don't set this property when sharing deep links on Facebook",
  },
  {
    value: '$og_redirect',
    label: '$og_redirect',
    tooltip:
      '(Advanced, not recommended) Set a custom URL that we redirect the social media robots to in order to retrieve all the appropriate tags',
  },
  {
    value: '$twitter_card',
    label: '$twitter_card',
    tooltip:
      'Set the Twitter card type of the link (e.g. player) (you must whitelist your deep link with the Twitter Card Validator)',
  },
  {
    value: '$twitter_site',
    label: '$twitter_site',
    tooltip: 'Set the site for Twitter',
  },
  {
    value: '$twitter_app_country',
    label: '$twitter_app_country',
    tooltip: 'Set the app country for the app card',
  },
  {
    value: '$twitter_player',
    label: '$twitter_player',
    tooltip: "Set the video player's URL. Defaults to the value of $og_video.",
  },
  {
    value: '$twitter_player_width',
    label: '$twitter_player_width',
    tooltip: "Set the player's width in pixels.",
  },
  {
    value: '$twitter_player_height',
    label: '$twitter_player_height',
    tooltip: "Set the player's height in pixels.",
  },
  {
    value: '$custom_meta_tags',
    label: '$custom_meta_tags',
    tooltip:
      'Valid stringified JSON dictionary of the tags’ keys and values. Example: "{"twitter:player:stream": "https://branch.io"}"',
  },
];

export const getPresetKeys = (flipperFlags: FlipperFlagValues): PresetKeys => {
  return PRESET_KEYS.filter((presetKey: PresetKey) => {
    if (presetKey.value === '$household_measurement_window_days' && !flipperFlags.household_measurement_window) {
      return false;
    }

    return true;
  });
};

export const validateSocialMediaConfig = (
  payload: {
    data: Record<string, any>;
  } = {
    data: {},
  },
  appConfig: Record<string, any> = {}
) => {
  const data = R.merge(
    {
      $og_title: appConfig.og_title,
      $og_description: appConfig.og_description,
      $og_image_url: appConfig.og_image_url,
    },
    payload.data
  );
  return !!(data.$og_title && data.$og_description);
};
export const validateAnalyticsTagsConfig = (payload: Record<string, any>): boolean => {
  return !!(payload.channel && payload.campaign && payload.feature);
};
export const validateDeepLinkingConfig = (payload: { data: Record<string, any> }): boolean => {
  return !R.isEmpty(R.keys(R.omit(PRESET_FIELDS, payload.data)));
};
export const mergePresetKeys = (
  presetKeys: PresetKeys = [],
  data: Record<string, string> = {},
  presets: string[] = ['']
) => {
  const newPresetKeys: PresetKeys = [];
  const presetKeysFlat: string[] = [];
  const dataKeys = Object.keys(data);
  presetKeys.forEach(function (presetKeyObj) {
    presetKeysFlat.push(presetKeyObj.value);
  });
  dataKeys.forEach((item) => {
    if (!presetKeysFlat.includes(item) && !presets.includes(item)) {
      newPresetKeys.push({
        value: item,
        label: item,
        tooltip: '',
      });
    }
  });
  return presetKeys.concat(newPresetKeys);
};

function validateDesktopConfig(data: Record<string, any> = {}, appConfig: Record<string, any> = {}): boolean {
  const { desktop_url, web_url } = appConfig;

  if (desktop_url || web_url) {
    return true;
  }

  // if neither $desktop_deepview or $desktop_url are in link data, default TMTA is selected which is enough
  if (!data.hasOwnProperty('$desktop_deepview') && !data.hasOwnProperty('$desktop_url')) {
    return true;
  }

  // TODO: change this validation once we change how these states are handled
  //  in the store
  // initial state where $desktop_deepview or $desktop_url might not be set to null
  if (
    (data.hasOwnProperty('$desktop_deepview') && data.$desktop_deepview !== undefined && data.$desktop_deepview !== null) ||
    (data.hasOwnProperty('$desktop_url') && data.$desktop_url !== undefined && data.$desktop_url !== null)
  ) {
    return true;
  }

  // if $desktop_deepview or $desktop_url has been selected, then we need to check if either value is undefined
  if (data.$desktop_deepview !== undefined && data.$desktop_url !== undefined) {
    return true;
  }

  return false;
}

type validateRedirectsConfigPayload = {
  data: {
    $ios_deepview?: string;
    $ios_url?: string;
    $android_deepview?: string;
    $android_url?: string;
    $desktop_deepview?: string;
    $desktop_url?: string;
  };
};
export const validateRedirectsConfig = (
  payload: validateRedirectsConfigPayload,
  appConfig: Record<string, any>
): boolean => {
  const { data = {} } = payload;
  const iosConfig = data.$ios_deepview || data.$ios_url || appConfig.ios_url || appConfig.web_url;
  const androidConfig = data.$android_deepview || data.$android_url || appConfig.android_url || appConfig.web_url;
  const desktopConfig = validateDesktopConfig(data, appConfig);
  return !!desktopConfig && !!iosConfig && !!androidConfig;
};
export const getLinkCreatorDataOnSecondAttempt = (updatedPayload: Record<string, any>): Record<string, any> => {
  return { ...updatedPayload, link_id: updatedPayload.id };
};
export const validateAdsAnalyticsTagsConfig = (payload: Record<string, any>): boolean => {
  return !!(payload.data['~channel'] && payload.data['~campaign']);
};
export const mapDispatchProps = (dispatch: (action: Record<string, any>) => Record<string, any>): Record<string, any> => {
  return {
    clearScraperData: () => dispatch(clearScraperData),
    fetchLinkData: (payload: Record<string, any>) => dispatch(fetchLinkData(payload)),
    fetchTuneSiteData: () => dispatch(fetchTuneSiteData()),
    getAndroidAppInfo: (payload: Record<string, any>) => dispatch(getAndroidAppInfo(payload)),
    getIOSAppInfo: (payload: Record<string, any>) => dispatch(getIOSAppInfo(payload)),
    getLinkById: (payload: Record<string, any>, type: string, links: Array<Record<string, any>>) =>
      dispatch(getLinkById(payload, type, links)),
    getTopCampaign: (payload: Record<string, any>) => dispatch(getTopCampaign(payload)),
    getTopChannel: (payload: Record<string, any>) => dispatch(getTopChannel(payload)),
    getTopFeature: (payload: Record<string, any>) => dispatch(getTopFeature(payload)),
    resetStore: () => dispatch(resetStore()),
    saveLink: (payload: Record<string, any>, type: string) => dispatch(saveLink(payload, type)),
    updateLinkData: (payload: Record<string, any>, forceUpdate: boolean) => dispatch(updateLinkData(payload, forceUpdate)),
    getEnabledAdNetworks: (payload: Record<string, any>) => dispatch(getEnabledAdNetworks(payload)),
    getAdOntology: () => dispatch(getAdOntology()),
    getAdLinks: () => dispatch(getAdLinks()),
    archiveAdsLink: (payload: { link_id: string; archive: boolean }) => dispatch(archiveAdsLink(payload)),
    getDruidFiltersByDimension: (dimensions: string[]) => dispatch(getDruidFiltersByDimension(null, dimensions)),
    loadSDKInfo: () => dispatch(loadSDKInfo()),
    loadSDKLatestInfo: () => dispatch(loadSDKLatestInfo()),
  };
};

/**
 * Searches network list for specific network based on $3p value
 *
 * @param {Array}  enabledNetworkList - list of networks
 * @param {string} $3p                - network machine_name
 * @return {Object} selected network
 */
export function getNetworkFromList(enabledNetworkList: AdPartner[], $3p: string): AdPartner | undefined {
  if (!enabledNetworkList || !$3p) {
    return;
  }

  const networkInfo = R.find((network) => {
    return network.value === $3p;
  }, enabledNetworkList);
  return networkInfo;
}

/**
 * Lookup link_macro_query_paramater in ad_ontology using branch_parameter
 *
 * @param {Array} ad_ontology - list of global branch ad params
 * @param {string} branch_parameter - name of the branch parameter to lookup
 * @return {string} link_macro_query_paramter to use as query param key
 */
export function getQueryKeyFromAdOntology(ad_ontology?: AdOntology[], branch_parameter: string): string | null | undefined {
  if (!ad_ontology) {
    return;
  }

  const ontology_parameters = Object.values(ad_ontology)?.find((item) => item.branch_parameter === branch_parameter);

  if (!ontology_parameters) {
    return;
  }

  return ontology_parameters.link_macro_query_parameter;
}

/**
 * Returns true if the link macros for the given branch parameters exist and are equal
 *
 * @param {Object} parametersList - list of link_only_parameters for the given network
 * @param {string} firstParameterName - name of first parameter being compared
 * @param {string} secondParameterName - name of second parameter being compared
 */
export function linkMacrosAreEqual(
  parametersList: Record<string, any>,
  firstParameterName: string,
  secondParameterName: string
) {
  const firstParameter = R.filter((e) => e.branch_parameter === firstParameterName, parametersList);

  if (!firstParameter.length) {
    return false;
  }

  const secondParameter = R.filter((e) => e.branch_parameter === secondParameterName, parametersList);

  if (!secondParameter.length) {
    return false;
  }

  return firstParameter[0].link_macro === secondParameter[0].link_macro;
}

/**
 * Creates templated query params object based on ad network specs
 * 1. combines postback_parameters and link_only_parameters
 * 2. for each parameter the key will be either the link_macro_query_parameter from ad_ontology or the parameter_name if it's a CUSTOM_LINK_MACRO
 * 3. for each parameter the value will be the link_macro (i.e. {apple_ifa})
 *
 * @param {Object}   network     - app ad network configuration
 * @param {Array}    ad_ontology - list of global ad parameters
 * @return {string}
 */
export function generateTemplateParamsObject(
  network: AdPartner | null | undefined,
  ad_ontology: Record<string, any>,
  threePPartnerName?: string,
  adFormat?: string,
  osType?: string
): Record<string, any> {
  if (!network) {
    return {};
  }

  let templatedParamsObj = {};
  const postbackParameters = network.postback_parameters || [];
  const linkOnlyParameters = network.link_only_parameters || [];
  const parameters = postbackParameters.concat(linkOnlyParameters);
  const isTikTokSANAppOnlyLink = threePPartnerName === TIKTOK_SAN_MACHINE_NAME && adFormat === AD_FORMATS.APP_ONLY;

  if (!R.isEmpty(parameters) && !isTikTokSANAppOnlyLink) {
    // iterate through postback_parameters and build link params based on info from ontology
    templatedParamsObj = parameters.reduce((queryObj, param) => {
      const { branch_parameter, link_macro, parameter_name = '' } = param;
      let queryKey = parameter_name.replace(/[{}]/g, '');
      const queryValue = link_macro;

      // if it's a custom link macro, just use the parameter_name. Otherwise lookup using the ad_ontology table
      if (branch_parameter !== 'CUSTOM_LINK_MACRO') {
        queryKey = getQueryKeyFromAdOntology(ad_ontology, branch_parameter);
      }

      if (!queryKey || !queryValue) {
        return queryObj;
      }

      queryObj[encodeURIComponent(queryKey)] = queryValue;
      return queryObj;
    }, {});
  }

  // Ad partner always needs to be added
  templatedParamsObj[encodeURIComponent('$3p')] = threePPartnerName;

  if (threePPartnerName === GOOGLE_ADWORDS_MACHINE_NAME) {
    templatedParamsObj[encodeURIComponent('$always_deeplink')] = false;
  }

  if (isDeeplinkNoAttributionPartner(threePPartnerName) && adFormat === AD_FORMATS.APP_ONLY) {
    templatedParamsObj[encodeURIComponent('$deeplink_no_attribution')] = true;
  }

  // INTENG-4469 add these params for facebook cross-platform links
  if (threePPartnerName === FACEBOOK_MACHINE_NAME && adFormat !== AD_FORMATS.APP_ONLY) {
    templatedParamsObj['~campaign'] = '{{campaign.name}}';
    templatedParamsObj['~campaign_id'] = '{{campaign.id}}';
    templatedParamsObj['~ad_set_name'] = '{{adset.name}}';
    templatedParamsObj['~ad_set_id'] = '{{adset.id}}';
    templatedParamsObj['~ad_name'] = '{{ad.name}}';
    templatedParamsObj['~ad_id'] = '{{ad.id}}';
  }

  // Removes extra params for OS specific linking
  if (osType === 'Android') {
    delete templatedParamsObj[encodeURIComponent('$idfa')];
    delete templatedParamsObj[encodeURIComponent('$idfa_sha1')];
    delete templatedParamsObj[encodeURIComponent('$idfa_md5')];
  } else if (osType === 'iOS') {
    delete templatedParamsObj[encodeURIComponent('$aaid')];
    delete templatedParamsObj[encodeURIComponent('$aaid_sha1')];
    delete templatedParamsObj[encodeURIComponent('$aaid_md5')];
  }

  return templatedParamsObj;
}

/**
 * Current link types: Marketing & Ads
 * add logic here when more types are available
 */
export const getLinkTypeFromPath = (pathname: string): string => {
  let link_type = 'MARKETING';

  if (pathname.indexOf('ads') > -1) {
    link_type = 'ADS';
  }

  return link_type;
};

/**
 * helper function to uri encode all key:values of an object
 */
function uriEncodeKeyValues(obj: Record<string, any> = {}): Record<string, any> {
  const encodedObj = {};

  for (const key in obj) {
    if (!obj.hasOwnProperty(key)) {
      continue;
    }

    const encodedKey = encodeURIComponent(key);
    const encodedVal = encodeURIComponent(obj[key]);
    encodedObj[encodedKey] = encodedVal;
  }

  return encodedObj;
}

/**
 * An impression link is a combination of postback_parameters, link_only_parameters, and link data
 * The impression query params should match clicks' params with a few extra params related to impression
 * There is an impression block list which removes unnecessary params (e.g. link_title, base_url), probably won't need to be changed in the future
 * Dashboard block list: https://github.com/BranchMetrics/dashboard/blob/master/client/js/main/support/constants.js#L168
 * 1. create base url: https://impression.link/impression?branch_key=[branch_key]
 * 2. remove blocked params from link data
 * 3. merge templatedParamsObject into link data
 * 4. query stringify them and append them to the base url
 * @param {Object} templatedParamsObj - Object containing templated parameters
 * @param {Object} link - link data
 * @return {string?} url with analytics tags appended
 */
export const createImpressionLink = (
  link: Record<string, any>,
  templatedParamsObj: Record<string, any>
): string | null | undefined => {
  const linkData = R.path(['data'], link);

  if (!linkData) {
    return '';
  }

  const { branch_key } = AppsStore.getApp();
  const IMPRESSION_DOMAIN = process.env.NODE_ENV === 'production' ? 'impression' : 'stage.impression';
  const url = `https://${IMPRESSION_DOMAIN}.link/impression?branch_key=${branch_key}`;

  if (!templatedParamsObj) {
    return url;
  }

  // need to encode linkData but not templated params. Templated params need to maintain their macros
  const tempLinkData = R.omit(IMPRESSION_PARAM_BLACKLIST, linkData);
  const encodedLinkData = uriEncodeKeyValues(tempLinkData);
  const mergedParams = R.merge(encodedLinkData, templatedParamsObj);
  const impressionQueryStr = queryString.stringify(mergedParams, {
    encode: false,
  });
  return `${url}&${impressionQueryStr}`;
};

const createGoogleLink = (link, queryStr) => {
  const { android_uri_scheme, ios_uri_scheme } = AppsStore.getApp();
  const secondary_ad_format = R.path(['data', '~secondary_ad_format'], link);
  const link_id = R.path(['data', '~id'], link) || '';
  const deeplink_path = R.path(['data', '$deeplink_path'], link);
  const pathname = deeplink_path || 'open';

  switch (secondary_ad_format) {
    case 'Google Universal App Campaign iOS':
      return `http://branch.io?idfa={md5_advertising_id}&lat={lat}&click_ts={click_ts}&click_url={click_url}&video_id={video_id}&campaign_id={campaign_id}&link_identifier=link-${link_id}`;

    case 'Google Universal App Campaign Android':
      return `http://branch.io?adid={adid}&lat={lat}&click_ts={click_ts}&click_url={click_url}&video_id={video_id}&campaign_id={campaign_id}&link_identifier=link-${link_id}`;

    case 'Google Search App Engagement Android':
    case 'Google Display App Engagement Android':
      return `${android_uri_scheme || 'scheme://'}${pathname}?link_click_id=link-${link_id}&${queryStr}`;

    case 'Google Display App Engagement iOS':
      return `${ios_uri_scheme || 'scheme://'}${pathname}?link_click_id=link-${link_id}&${queryStr}`;

    default:
      return '';
  }
};

const getTuneLinkProperties = (
  queryObj: Record<string, any>,
  linkData: Record<string, any>,
  subdomains: TuneOptionsByPlatform,
  siteIds: TuneOptionsByPlatform,
  action?: 'click' | 'impression'
): {
  baseUrl: string;
  queryStr: string;
} => {
  const domain = getTuneDomain(subdomains);
  const tuneBase = getTuneBase(siteIds, action);
  const tuneQueryStr = expandTuneQueryParams(
    linkData,
    queryString.stringify(buildTuneQueryObject(queryObj), {
      encode: false,
    })
  );
  return {
    baseUrl: `${domain}/`,
    queryStr: `${tuneBase}&${tuneQueryStr}`,
  };
};

export const prefixHttps = (url: string): string => {
  if (typeof url !== 'string') {
    return '';
  }

  if (typeof url === 'string' && url.substring(0, 5) !== 'https') {
    // biome-ignore lint/suspicious/noDoubleEquals: ignored using `--suppress`
    if (url.substring(0, 4) == 'http') {
      return url.replace('http', 'https');
    }

    return 'https://' + url;
  }

  return url;
};
export const removeFieldFromObject = (queryObj: any, fieldNames: string[]): any => {
  if (!queryObj) {
    return queryObj;
  }

  fieldNames.forEach((fieldName) => {
    delete queryObj[fieldName];
  });
  return queryObj;
};

/**
 * Returns five properties
 * 1. baseUrl: excludes query parameters
 * 2. fullUrl: includes query parameters
 * 3. queryStr: the query param string for displaying separately from the baseUrl
 *        NOTE: When creating a Tune link, this will include the base of the URL
 *              not just the query param part. Therefore you will not need to put
 *              a '?' before it when using the Tune queryStr.
 * 4. isBranchLink: whether the link is a branch link. Google doesn't allow branch links for certain ad formats
 *
 * NOTE: Google links that use non-branch link only return a fullUrl
 */
export const buildAdLinks = (
  link: Record<string, any>,
  queryObj: Record<string, any>,
  displayAsTuneLink?: boolean,
  subdomains?: TuneOptionsByPlatform,
  siteIds?: TuneOptionsByPlatform,
  action?: 'click' | 'impression'
): {
  baseUrl: string;
  fullUrl: string;
  queryStr: string;
  isBranchLink: boolean;
} => {
  let baseUrl = '';
  let fullUrl = '';
  let isBranchLink = false;
  // 1. Get query strings to return
  let queryStr = queryString.stringify(queryObj, {
    encode: false,
  });

  // 1b. Tune links need special query params added:
  if (displayAsTuneLink && siteIds && subdomains) {
    ({ baseUrl, queryStr } = getTuneLinkProperties(queryObj, link.data, subdomains, siteIds, action));
  }

  // 2. Google - special case
  // Google AdWords requires non-branch links
  const threeP = R.path(['data', '$3p'], link);

  if (threeP === GOOGLE_ADWORDS_MACHINE_NAME) {
    fullUrl = createGoogleLink(link, queryStr);

    // if google link is created, queryStr will not be used
    if (fullUrl) {
      queryStr = '';
    }
  }

  // 3. Set normal case return values
  if (!fullUrl) {
    if (!baseUrl) {
      baseUrl = R.path(['data', '+url'], link);
    }

    baseUrl = prefixHttps(baseUrl);

    if (!queryStr) {
      fullUrl = baseUrl;
    } else {
      fullUrl = `${baseUrl}${displayAsTuneLink ? '' : '?'}${queryStr}`;
    }

    isBranchLink = true;
  }

  return {
    baseUrl,
    fullUrl,
    queryStr,
    isBranchLink,
  };
};
// Ad partners that require special logic
const custom_ad_partners = {
  a_facebook: 'FACEBOOK',
};

/**
 * secondary ad formats options are a product of ad partner and ad format
 */
export const getSecondaryAdFormatOptions = ($3p: string, ad_format: string): string | null | undefined => {
  if (!$3p || !ad_format) {
    return;
  }

  const partner = custom_ad_partners[$3p] || 'OTHER';
  return R.path([partner, ad_format], SECONDARY_AD_FORMATS);
};
export const sortByHeader = (active_header: string, data: Array<Record<string, any>>): Array<Record<string, any>> => {
  // need to sort date in descending order
  if (active_header === 'date') {
    const dateSortFn = (a, b) => {
      return b.date - a.date;
    };

    return data.sort(dateSortFn);
  }

  return R.sortBy((row) => {
    const header_row = R.prop(active_header, row);

    if (typeof header_row === 'string') {
      return R.toLower(header_row || '');
    }

    return header_row;
  }, data);
};

/**
 * Returns a boolean of whether query params match link data
 * @param {String} key   - key to match
 * @param {Object} query - query params in object format
 * @param {Object} data  - link data object
 */
export function dataSetFromQuery(key: string, query: Record<string, any>, data: Record<string, any>): boolean {
  return query[key] && data[key] === query[key];
}

export function needsOsType(enabledNetworks: Array<Record<string, any>>, threeP: string): boolean {
  if (!enabledNetworks.length) {
    return false;
  }

  const network = getNetworkFromList(enabledNetworks, threeP);

  if (!network) {
    return false;
  }

  const linkOnlyParameters = network.link_only_parameters || {};
  return (
    linkMacrosAreEqual(linkOnlyParameters, 'CLICK_AAID', 'CLICK_IDFA') ||
    linkMacrosAreEqual(linkOnlyParameters, 'CLICK_AAID_SHA1', 'CLICK_IDFA_SHA1') ||
    linkMacrosAreEqual(linkOnlyParameters, 'CLICK_AAID_MD5', 'CLICK_IDFA_MD5')
  );
}
